import React, { useState, useEffect, useContext } from 'react';
import './RatingModal.css';
import { StoreContext } from '../../Context/StoreContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/modalUtils';
import { createPortal } from 'react-dom';

const RatingModal = ({ restaurant, isOpen, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [userOrders, setUserOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [existingReview, setExistingReview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { url } = useContext(StoreContext);

  // Effect to manage body scroll locking
  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
    
    // Cleanup function to ensure we unlock scroll when component unmounts
    return () => {
      unlockBodyScroll();
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && restaurant) {
      fetchUserOrders();
      fetchExistingReview();
    }
  }, [isOpen, restaurant]);

  const fetchUserOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${url}/api/order/userorders`, {
        limit: 100
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const allOrders = response.data.data?.orders || [];
        
        // Filter for completed orders from this specific restaurant
        const restaurantOrders = allOrders.filter(order => {
          const isCorrectRestaurant = order.restaurant && 
            (order.restaurant._id === restaurant._id || order.restaurant === restaurant._id);
              // Check if order is completed based on delivery type
    const isCompleted = order.deliveryType === 'pickup' 
      ? order.status === 'Picked Up'
      : ['Delivered', 'Completed'].includes(order.status);
    
    return isCorrectRestaurant && isCompleted;
        });
        
        setUserOrders(restaurantOrders);
        if (restaurantOrders.length > 0) {
          setSelectedOrder(restaurantOrders[0]._id);
        }
      }
    } catch (error) {
      console.error('Error fetching user orders:', error);
    }
  };

  const fetchExistingReview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${url}/api/reviews/restaurant/${restaurant._id}/my-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.review) {
        const existingReview = response.data.review;
        setExistingReview(existingReview);
        setRating(existingReview.rating);
        setReview(existingReview.review || '');
      }
    } catch (error) {
      // No existing review found, which is fine
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!selectedOrder) {
      toast.error('Please select an order to rate');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${url}/api/reviews/restaurant/${restaurant._id}`,
        {
          rating,
          review: review.trim(),
          orderId: selectedOrder
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(existingReview ? 'Review updated successfully!' : 'Review submitted successfully!');
        onClose();
        // Refresh the page to show updated rating
        window.location.reload();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        className={`star ${star <= (hoverRating || rating) ? 'filled' : ''}`}
        onClick={() => setRating(star)}
        onMouseEnter={() => setHoverRating(star)}
        onMouseLeave={() => setHoverRating(0)}
      >
        ⭐
      </button>
    ));
  };

  // Return null if modal is not open
  if (!isOpen) return null;

  // Use createPortal to render the modal at the root level of the DOM
  return createPortal(
    <div className="rating-modal-overlay" onClick={onClose}>
      <div className="rating-modal" onClick={e => e.stopPropagation()}>
        <div className="rating-modal-header">
          <h3>{existingReview ? 'Update Your Review' : 'Rate'} {restaurant?.name}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="rating-modal-body">
          {userOrders.length === 0 ? (
            <div className="no-orders-message">
              <p>You need to have completed an order from this restaurant to leave a review.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="rating-section">
                <label>Your Rating:</label>
                <div className="stars-container">
                  {renderStars()}
                  <span className="rating-text">
                    {rating === 0 ? 'Select a rating' : 
                     rating === 1 ? 'Poor' :
                     rating === 2 ? 'Fair' :
                     rating === 3 ? 'Good' :
                     rating === 4 ? 'Very Good' : 'Excellent'}
                  </span>
                </div>
              </div>

              <div className="order-selection">
                <label htmlFor="order-select">Select Order:</label>
                <select
                  id="order-select"
                  value={selectedOrder}
                  onChange={(e) => setSelectedOrder(e.target.value)}
                  required
                >
                  {userOrders.map((order) => (
                    <option key={order._id} value={order._id}>
                      Order from {new Date(order.createdAt).toLocaleDateString()} - ${order.total}
                    </option>
                  ))}
                </select>
              </div>

              <div className="review-section">
                <label htmlFor="review-text">Your Review (Optional):</label>
                <textarea
                  id="review-text"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  placeholder="Share your experience with this restaurant..."
                  maxLength="500"
                  rows="4"
                />
                <div className="character-count">{review.length}/500</div>
              </div>

              <div className="rating-modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting || rating === 0}
                >
                  {isSubmitting ? 'Submitting...' : (existingReview ? 'Update Review' : 'Submit Review')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body // Render directly in the body element
  );
};

export default RatingModal; 